import { useCallback, useRef, useState } from 'react';

import { PhotosCommandApi, PhotosQueryApi } from '@lib/api/endpoints';
import {
  detectFileType,
  FILE_EXTENSION_TO_MIME_TYPE,
  getFileExtension,
} from '@lib/utils/files';
import { uuid } from '@lib/utils/idHelper';

const MAX_SIZE_IN_MB = 5;

export const getDownloadURL = (uploadUrl: UploadUrl): string => {
  return `${process.env.NEXT_PUBLIC_CF_PHOTOS_BASE_URL}/${uploadUrl.fields.key}`;
};

type UseFileUploadOptions = {
  fileExtensions?: string[];
};

export const useFileUpload = (maxSize = MAX_SIZE_IN_MB): UseFileUpload => {
  const [fileErrorMessage, setFileErrorMessage] = useState('');
  const fileErrorMessageRef = useRef('');

  const validateFile = useCallback(
    async (
      file: File,
      options?: UseFileUploadOptions,
      invalidCallback: () => void = null
    ): Promise<boolean> => {
      const fileSizeInMb = file.size / (1024 * 1024);
      const isValidSize = fileSizeInMb < maxSize;
      if (!isValidSize) {
        if (invalidCallback) invalidCallback();
        const errorMessage = `File size should be ${maxSize}MB or less`;
        fileErrorMessageRef.current = errorMessage;
        setFileErrorMessage(errorMessage);
        return false;
      }
      if (options?.fileExtensions?.length) {
        const allowedMimeTypes = options.fileExtensions.map(
          (ext) => FILE_EXTENSION_TO_MIME_TYPE[ext?.toLowerCase()] ?? ext
        );
        const fileMimeType = await detectFileType(file);
        const isValidType = allowedMimeTypes.includes(fileMimeType);
        if (!isValidType) {
          const errorMessage = `File type must be one of the following: ${options.fileExtensions.join(
            ', '
          )}`;
          fileErrorMessageRef.current = errorMessage;
          setFileErrorMessage(errorMessage);
          return false;
        }
      }
      setFileErrorMessage('');
      fileErrorMessageRef.current = '';
      return true;
    },
    [maxSize]
  );

  const uploadFile = useCallback(
    async (
      file: File | Blob,
      uploadUrlCallbackFn: (
        uploadUrl?: UploadUrl
      ) => void | Promise<void> = null,
      onSuccessCallback: (uploadUrl: UploadUrl) => void = null,
      onFailCallback: (error?) => void = null,
      onFinishCallback: () => void = null,
      fileName: string = null,
      bucket?: BucketType
    ) => {
      if (!file) return;
      try {
        const uploadUrl = await requestUploadUrl(file, fileName, bucket);
        await PhotosCommandApi.uploadImage(uploadUrl, file);
        if (uploadUrlCallbackFn) await uploadUrlCallbackFn(uploadUrl);
        if (onSuccessCallback) onSuccessCallback(uploadUrl);
      } catch (error) {
        if (onFailCallback) onFailCallback(error);
      }

      if (onFinishCallback) onFinishCallback();
    },
    []
  );

  return {
    validateFile,
    uploadFile,
    fileErrorMessage,
    fileErrorMessageRef,
  };
};

type UploadFileParams = {
  file: File | Blob;
  fileName?: string;
  bucket?: BucketType;
};

export const uploadFileAndReturnUrl = async ({
  file,
  fileName,
  bucket,
}: UploadFileParams) => {
  if (!file) return;
  const uploadUrl = await requestUploadUrl(file, fileName, bucket);
  await PhotosCommandApi.uploadImage(uploadUrl, file);
  const downloadUrl = getDownloadURL(uploadUrl);

  return { downloadUrl, rawUploadUrlObject: uploadUrl };
};

const requestUploadUrl = (
  file: File | Blob,
  fileName: string = null,
  bucket?: BucketType
): Promise<UploadUrl> => {
  const uniqueFileName = () => {
    return file && `${uuid()}.${getFileExtension(file)}`;
  };
  if (!file) return;
  return PhotosQueryApi.getUploadUrl(
    fileName ?? uniqueFileName(),
    file.type,
    bucket
  );
};
